import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdUndo, MdRedo, MdFormatBold, MdFormatItalic, MdFormatUnderlined, MdStrikethroughS,
    MdSubscript, MdSuperscript, MdFormatColorText, MdFormatColorFill,
    MdFormatAlignLeft, MdFormatAlignCenter, MdFormatAlignRight, MdFormatAlignJustify,
    MdFormatListBulleted, MdFormatListNumbered, MdFormatIndentIncrease, MdFormatIndentDecrease,
    MdLink, MdLinkOff, MdTableChart, MdHorizontalRule, MdFormatQuote, MdCode, MdFormatClear,
    MdFullscreen, MdFullscreenExit, MdFormatTextdirectionRToL, MdFormatTextdirectionLToR,
} from 'react-icons/md'
import { sanitizeInlineStyle } from '../../utils/richTextHtml'
import { useCurrent } from '../../hooks'

const TEXT_COLORS = [
    '#000000', '#434343', '#666666', '#999999', '#cccccc', '#ffffff',
    '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb',
    '#4f46e5', '#9333ea', '#db2777', '#7f1d1d', '#14532d', '#1e3a8a',
]
const HIGHLIGHT_COLORS = [
    'transparent', '#fef08a', '#fde68a', '#bbf7d0', '#a5f3fc', '#bfdbfe',
    '#ddd6fe', '#fbcfe8', '#fecaca', '#fed7aa', '#e2e8f0', '#d9f99d',
]
const FONT_FAMILIES = ['Cairo', 'Tahoma', 'Arial', 'Times New Roman', 'Courier New', 'Verdana']
const FONT_SIZES = [
    { value: '1', ar: 'صغير جداً', en: 'Tiny' },
    { value: '2', ar: 'صغير', en: 'Small' },
    { value: '3', ar: 'عادي', en: 'Normal' },
    { value: '4', ar: 'متوسط', en: 'Medium' },
    { value: '5', ar: 'كبير', en: 'Large' },
    { value: '6', ar: 'كبير جداً', en: 'X-Large' },
    { value: '7', ar: 'ضخم', en: 'Huge' },
]
const BLOCK_FORMATS = [
    { value: 'p', ar: 'فقرة', en: 'Paragraph' },
    { value: 'h1', ar: 'عنوان 1', en: 'Heading 1' },
    { value: 'h2', ar: 'عنوان 2', en: 'Heading 2' },
    { value: 'h3', ar: 'عنوان 3', en: 'Heading 3' },
    { value: 'blockquote', ar: 'اقتباس', en: 'Quote' },
    { value: 'pre', ar: 'كود', en: 'Code' },
]
const STATE_COMMANDS = [
    'bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript',
    'insertUnorderedList', 'insertOrderedList',
    'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull',
]
const BLOCK_SELECTOR = 'p,div,li,h1,h2,h3,h4,h5,h6,blockquote,pre,td,th'

// ---- تنظيف المحتوى الملصوق (من Word / المتصفح) ----
const PASTE_ALLOWED_TAGS = new Set([
    'P', 'DIV', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'SUB', 'SUP', 'SPAN', 'FONT',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'CODE', 'A',
    'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'HR',
])
const PASTE_DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'XML', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'IMG', 'VIDEO', 'AUDIO', 'CANVAS', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'FORM'])
const PASTE_ALLOWED_ATTRS = new Set(['href', 'style', 'dir', 'align', 'colspan', 'rowspan', 'color', 'face', 'size'])

const cleanPastedNode = (node) => {
    Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 8) { child.remove(); return; }
        if (child.nodeType !== 1) return;
        const tag = child.tagName.toUpperCase();
        if (PASTE_DROP_TAGS.has(tag) || tag.includes(':')) { child.remove(); return; }
        cleanPastedNode(child);
        if (!PASTE_ALLOWED_TAGS.has(tag)) {
            child.replaceWith(...Array.from(child.childNodes));
            return;
        }
        Array.from(child.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            if (!PASTE_ALLOWED_ATTRS.has(name)) { child.removeAttribute(attr.name); return; }
            if (name === 'href' && !/^(https?:|mailto:|tel:)/i.test(attr.value.trim())) child.removeAttribute(attr.name);
            if (name === 'style') {
                const safe = sanitizeInlineStyle(attr.value);
                if (safe) child.setAttribute('style', safe);
                else child.removeAttribute('style');
            }
        });
        if (tag === 'A') {
            child.setAttribute('target', '_blank');
            child.setAttribute('rel', 'noreferrer');
        }
    });
};

const cleanPastedHtml = (html) => {
    const template = document.createElement('template');
    template.innerHTML = String(html || '')
        .replace(/<!--\s*StartFragment\s*-->|<!--\s*EndFragment\s*-->/gi, '');
    cleanPastedNode(template.content);
    const wrapper = document.createElement('div');
    wrapper.appendChild(template.content.cloneNode(true));
    return wrapper.innerHTML;
};

const buildTableHtml = (rows, cols) => {
    const cell = '<td style="border: 1px solid #cbd5e1; padding: 6px 8px; min-width: 60px; vertical-align: top"><br></td>';
    const row = `<tr>${cell.repeat(cols)}</tr>`;
    return `<table style="border-collapse: collapse; width: 100%"><tbody>${row.repeat(rows)}</tbody></table><p><br></p>`;
};

const ToolbarButton = ({ title, active, onClick, children, disabled }) => (
    <button
        type="button"
        title={title}
        aria-label={title}
        aria-pressed={active ? 'true' : undefined}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        className={`h-7 min-w-7 px-1.5 inline-flex items-center justify-center rounded-md text-slate-600 transition-colors disabled:opacity-40 ${active ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-slate-100 hover:text-slate-900'}`}
    >
        {children}
    </button>
);

const Divider = () => <span className="w-px h-5 bg-slate-200 mx-0.5 shrink-0" />;

const MwRichEditor = ({
    id,
    value,
    onChange,
    placeholder,
    rows,
    minHeight,
    invalid,
    classNameCustom = '',
    dir = 'auto',
    textAlign,
}) => {
    const { currentLangId } = useCurrent();
    const isAr = currentLangId !== 'en';
    const plainPasteRef = useRef(false);
    const resolvedValue = value == null ? '' : String(value);
    const rootRef = useRef(null);
    const editorRef = useRef(null);
    const selectionRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [activeStates, setActiveStates] = useState({});
    const [blockValue, setBlockValue] = useState('p');
    const [openMenu, setOpenMenu] = useState('');
    const [tableHover, setTableHover] = useState({ rows: 0, cols: 0 });
    const [fullscreen, setFullscreen] = useState(false);
    const [counts, setCounts] = useState({ words: 0, chars: 0 });

    const minHeightPx = useMemo(() => {
        if (Number.isFinite(Number(minHeight)) && Number(minHeight) > 0) return Number(minHeight);
        const base = Number.isFinite(Number(rows)) && Number(rows) > 0 ? Number(rows) * 22 : 140;
        return Math.max(120, Math.min(420, base));
    }, [minHeight, rows]);

    const resolvedTextAlign = textAlign || (dir === 'rtl' ? 'right' : dir === 'ltr' ? 'left' : 'start');

    const updateCounts = (el) => {
        const text = (el?.innerText ?? el?.textContent ?? '').replace(/ /g, ' ');
        const trimmed = text.trim();
        setIsEmpty(trimmed.length === 0 && !el?.querySelector?.('table,hr,li'));
        setCounts({ words: trimmed ? trimmed.split(/\s+/).length : 0, chars: trimmed.length });
    };

    const emitChange = useCallback(() => {
        const el = editorRef.current;
        updateCounts(el);
        onChange?.({ target: { name: id, value: el?.innerHTML ?? '' } });
    }, [id, onChange]);

    const rememberSelection = useCallback(() => {
        try {
            const root = editorRef.current;
            const sel = window.getSelection?.();
            if (!root || !sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            if (!root.contains(range.commonAncestorContainer)) return;
            selectionRef.current = range.cloneRange();
        } catch { }
    }, []);

    const restoreSelection = () => {
        try {
            const sel = window.getSelection?.();
            const range = selectionRef.current;
            if (!sel || !range) return;
            sel.removeAllRanges();
            sel.addRange(range);
        } catch { }
    };

    const getSelectionBlock = () => {
        const root = editorRef.current;
        const sel = window.getSelection?.();
        if (!root || !sel || sel.rangeCount === 0) return null;
        let node = sel.getRangeAt(0).startContainer;
        if (node?.nodeType === 3) node = node.parentElement;
        const block = node?.closest?.(BLOCK_SELECTOR);
        return block && root.contains(block) && block !== root ? block : null;
    };

    const refreshToolbarState = useCallback(() => {
        const root = editorRef.current;
        const sel = window.getSelection?.();
        if (!root || !sel || sel.rangeCount === 0 || !root.contains(sel.anchorNode)) return;
        const next = {};
        STATE_COMMANDS.forEach((cmd) => {
            try { next[cmd] = document.queryCommandState(cmd); } catch { next[cmd] = false; }
        });
        const block = getSelectionBlock();
        const blockTag = block?.closest?.('h1,h2,h3,blockquote,pre')?.tagName?.toLowerCase();
        next.link = Boolean((sel.anchorNode?.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode)?.closest?.('a'));
        next.rtl = block?.closest?.('[dir]')?.getAttribute('dir') === 'rtl';
        next.ltr = block?.closest?.('[dir]')?.getAttribute('dir') === 'ltr';
        setActiveStates(next);
        setBlockValue(blockTag || 'p');
    }, []);

    const exec = (cmd, arg) => {
        const el = editorRef.current;
        el?.focus?.();
        restoreSelection();
        try {
            document.execCommand('styleWithCSS', false, true);
            document.execCommand(cmd, false, arg);
        } catch { }
        rememberSelection();
        emitChange();
        refreshToolbarState();
    };

    const setBlockDirection = (direction) => {
        editorRef.current?.focus?.();
        restoreSelection();
        let block = getSelectionBlock();
        if (!block) {
            try { document.execCommand('formatBlock', false, '<p>'); } catch { }
            block = getSelectionBlock();
        }
        if (!block) return;
        const target = block.tagName === 'LI' ? (block.closest('ul,ol') || block) : block;
        if (target.getAttribute('dir') === direction) target.removeAttribute('dir');
        else target.setAttribute('dir', direction);
        rememberSelection();
        emitChange();
        refreshToolbarState();
    };

    const insertLink = () => {
        restoreSelection();
        const existing = (() => {
            const sel = window.getSelection?.();
            const node = sel?.anchorNode?.nodeType === 3 ? sel.anchorNode.parentElement : sel?.anchorNode;
            return node?.closest?.('a')?.getAttribute('href') || '';
        })();
        const raw = window.prompt(isAr ? 'أدخل الرابط:' : 'Enter URL:', existing || 'https://');
        if (!raw || !raw.trim() || raw.trim() === 'https://') return;
        let url = raw.trim();
        if (!/^(https?:|mailto:|tel:)/i.test(url)) url = `https://${url}`;
        const sel = window.getSelection?.();
        if (sel && sel.rangeCount && sel.isCollapsed && !existing) {
            exec('insertHTML', `<a href="${url.replace(/"/g, '%22')}" target="_blank" rel="noreferrer">${url.replace(/</g, '&lt;')}</a>&nbsp;`);
            return;
        }
        exec('createLink', url);
        editorRef.current?.querySelectorAll('a:not([target])').forEach((a) => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noreferrer');
        });
        emitChange();
    };

    const insertTable = (r, c) => {
        setOpenMenu('');
        setTableHover({ rows: 0, cols: 0 });
        exec('insertHTML', buildTableHtml(r, c));
    };

    // مزامنة القيمة الخارجية (مثلاً بعد الإرسال أو فتح التعديل) لما المحرر مش متركز عليه.
    useEffect(() => {
        const el = editorRef.current;
        if (!el || isFocused) return;
        if (el.innerHTML !== resolvedValue) {
            el.innerHTML = resolvedValue;
        }
        updateCounts(el);
    }, [isFocused, resolvedValue]);

    useEffect(() => {
        if (!isFocused) return undefined;
        const handler = () => {
            rememberSelection();
            refreshToolbarState();
        };
        document.addEventListener('selectionchange', handler);
        return () => document.removeEventListener('selectionchange', handler);
    }, [isFocused, refreshToolbarState, rememberSelection]);

    useEffect(() => {
        if (!openMenu) return undefined;
        const close = (e) => {
            if (!rootRef.current?.contains(e.target) || !e.target.closest?.('[data-rich-menu]')) setOpenMenu('');
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [openMenu]);

    useEffect(() => {
        if (!fullscreen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const onKeyDown = (e) => {
        const mod = e.ctrlKey || e.metaKey;
        plainPasteRef.current = mod && e.shiftKey && e.key.toLowerCase() === 'v';
        if (mod && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            rememberSelection();
            insertLink();
            return;
        }
        if (e.key === 'Tab') {
            const inList = Boolean(getSelectionBlock()?.closest?.('li'));
            if (inList) {
                e.preventDefault();
                exec(e.shiftKey ? 'outdent' : 'indent');
            }
        }
    };

    const onPaste = (e) => {
        e.preventDefault();
        const html = e.clipboardData?.getData('text/html') || '';
        const text = e.clipboardData?.getData('text/plain') || '';
        const plainOnly = plainPasteRef.current;
        plainPasteRef.current = false;
        if (html && !plainOnly) {
            const cleaned = cleanPastedHtml(html);
            if (cleaned.trim()) {
                exec('insertHTML', cleaned);
                return;
            }
        }
        exec('insertText', text);
    };

    const t = (ar, en) => (isAr ? ar : en);
    const selectClass = 'h-7 rounded-md border border-slate-200 bg-white px-1.5 text-[11px] text-slate-700 hover:border-slate-300 focus:outline-none focus:border-indigo-400 cursor-pointer';

    const colorMenu = (key, colors, command, icon, title) => (
        <div className="relative" data-rich-menu>
            <ToolbarButton title={title} active={openMenu === key} onClick={() => setOpenMenu(openMenu === key ? '' : key)}>
                {icon}
            </ToolbarButton>
            {openMenu === key && (
                <div className="absolute top-8 z-[90] bg-white border border-slate-200 rounded-lg shadow-lg p-2 grid grid-cols-6 gap-1 w-[164px]" style={{ insetInlineStart: 0 }}>
                    {colors.map((color) => (
                        <button
                            key={color}
                            type="button"
                            title={color === 'transparent' ? t('بدون', 'None') : color}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setOpenMenu(''); exec(command, color); }}
                            className="w-5 h-5 rounded border border-slate-200 hover:scale-110 transition-transform"
                            style={{ background: color === 'transparent' ? 'repeating-linear-gradient(45deg,#fff,#fff 3px,#fecaca 3px,#fecaca 5px)' : color }}
                        />
                    ))}
                    <label className="col-span-6 mt-1 flex items-center justify-between gap-2 text-[10px] text-slate-500 cursor-pointer" onMouseDown={(e) => e.stopPropagation()}>
                        {t('لون مخصص', 'Custom')}
                        <input
                            type="color"
                            className="w-8 h-5 p-0 border-0 bg-transparent cursor-pointer"
                            onChange={(e) => { const c = e.target.value; setOpenMenu(''); exec(command, c); }}
                        />
                    </label>
                </div>
            )}
        </div>
    );

    return (
        <>
        {fullscreen && <div className="fixed inset-0 z-[9999] bg-slate-900/50" onClick={() => setFullscreen(false)} />}
        <div
            ref={rootRef}
            className={fullscreen
                ? 'fixed inset-3 md:inset-8 z-[10000] flex flex-col bg-white rounded-xl shadow-2xl border border-slate-300'
                : `relative w-full rounded-lg border bg-white ${invalid ? 'border-red-500' : 'border-slate-200'} focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-shadow ${classNameCustom}`}
        >
            <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-slate-200 bg-slate-50 rounded-t-lg" dir={isAr ? 'rtl' : 'ltr'}>
                <ToolbarButton title={t('تراجع (Ctrl+Z)', 'Undo (Ctrl+Z)')} onClick={() => exec('undo')}><MdUndo size={16} /></ToolbarButton>
                <ToolbarButton title={t('إعادة (Ctrl+Y)', 'Redo (Ctrl+Y)')} onClick={() => exec('redo')}><MdRedo size={16} /></ToolbarButton>
                <Divider />
                <select
                    title={t('نوع الفقرة', 'Block style')}
                    className={`${selectClass} w-[88px]`}
                    value={blockValue}
                    onMouseDown={rememberSelection}
                    onChange={(e) => exec('formatBlock', `<${e.target.value}>`)}
                >
                    {BLOCK_FORMATS.map((opt) => <option key={opt.value} value={opt.value}>{isAr ? opt.ar : opt.en}</option>)}
                </select>
                <select
                    title={t('الخط', 'Font')}
                    className={`${selectClass} w-[96px]`}
                    defaultValue=""
                    onMouseDown={rememberSelection}
                    onChange={(e) => { if (e.target.value) exec('fontName', e.target.value); e.target.value = ''; }}
                >
                    <option value="" disabled>{t('الخط', 'Font')}</option>
                    {FONT_FAMILIES.map((font) => <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>)}
                </select>
                <select
                    title={t('حجم الخط', 'Font size')}
                    className={`${selectClass} w-[80px]`}
                    defaultValue=""
                    onMouseDown={rememberSelection}
                    onChange={(e) => { if (e.target.value) exec('fontSize', e.target.value); e.target.value = ''; }}
                >
                    <option value="" disabled>{t('الحجم', 'Size')}</option>
                    {FONT_SIZES.map((size) => <option key={size.value} value={size.value}>{isAr ? size.ar : size.en}</option>)}
                </select>
                <Divider />
                <ToolbarButton title={t('عريض (Ctrl+B)', 'Bold (Ctrl+B)')} active={activeStates.bold} onClick={() => exec('bold')}><MdFormatBold size={17} /></ToolbarButton>
                <ToolbarButton title={t('مائل (Ctrl+I)', 'Italic (Ctrl+I)')} active={activeStates.italic} onClick={() => exec('italic')}><MdFormatItalic size={17} /></ToolbarButton>
                <ToolbarButton title={t('تسطير (Ctrl+U)', 'Underline (Ctrl+U)')} active={activeStates.underline} onClick={() => exec('underline')}><MdFormatUnderlined size={17} /></ToolbarButton>
                <ToolbarButton title={t('يتوسطه خط', 'Strikethrough')} active={activeStates.strikeThrough} onClick={() => exec('strikeThrough')}><MdStrikethroughS size={17} /></ToolbarButton>
                <ToolbarButton title={t('منخفض', 'Subscript')} active={activeStates.subscript} onClick={() => exec('subscript')}><MdSubscript size={17} /></ToolbarButton>
                <ToolbarButton title={t('مرتفع', 'Superscript')} active={activeStates.superscript} onClick={() => exec('superscript')}><MdSuperscript size={17} /></ToolbarButton>
                {colorMenu('fore', TEXT_COLORS, 'foreColor', <MdFormatColorText size={17} />, t('لون النص', 'Text color'))}
                {colorMenu('hilite', HIGHLIGHT_COLORS, 'hiliteColor', <MdFormatColorFill size={17} />, t('لون التظليل', 'Highlight'))}
                <Divider />
                <ToolbarButton title={t('محاذاة لليمين', 'Align right')} active={activeStates.justifyRight} onClick={() => exec('justifyRight')}><MdFormatAlignRight size={17} /></ToolbarButton>
                <ToolbarButton title={t('توسيط', 'Center')} active={activeStates.justifyCenter} onClick={() => exec('justifyCenter')}><MdFormatAlignCenter size={17} /></ToolbarButton>
                <ToolbarButton title={t('محاذاة لليسار', 'Align left')} active={activeStates.justifyLeft} onClick={() => exec('justifyLeft')}><MdFormatAlignLeft size={17} /></ToolbarButton>
                <ToolbarButton title={t('ضبط', 'Justify')} active={activeStates.justifyFull} onClick={() => exec('justifyFull')}><MdFormatAlignJustify size={17} /></ToolbarButton>
                <ToolbarButton title={t('اتجاه من اليمين لليسار', 'Right-to-left')} active={activeStates.rtl} onClick={() => setBlockDirection('rtl')}><MdFormatTextdirectionRToL size={17} /></ToolbarButton>
                <ToolbarButton title={t('اتجاه من اليسار لليمين', 'Left-to-right')} active={activeStates.ltr} onClick={() => setBlockDirection('ltr')}><MdFormatTextdirectionLToR size={17} /></ToolbarButton>
                <Divider />
                <ToolbarButton title={t('قائمة نقطية', 'Bulleted list')} active={activeStates.insertUnorderedList} onClick={() => exec('insertUnorderedList')}><MdFormatListBulleted size={17} /></ToolbarButton>
                <ToolbarButton title={t('قائمة مرقمة', 'Numbered list')} active={activeStates.insertOrderedList} onClick={() => exec('insertOrderedList')}><MdFormatListNumbered size={17} /></ToolbarButton>
                <ToolbarButton title={t('إنقاص المسافة البادئة', 'Decrease indent')} onClick={() => exec('outdent')}><MdFormatIndentDecrease size={17} /></ToolbarButton>
                <ToolbarButton title={t('زيادة المسافة البادئة', 'Increase indent')} onClick={() => exec('indent')}><MdFormatIndentIncrease size={17} /></ToolbarButton>
                <Divider />
                <ToolbarButton title={t('إدراج رابط (Ctrl+K)', 'Insert link (Ctrl+K)')} active={activeStates.link} onClick={insertLink}><MdLink size={17} /></ToolbarButton>
                <ToolbarButton title={t('إزالة الرابط', 'Remove link')} onClick={() => exec('unlink')}><MdLinkOff size={17} /></ToolbarButton>
                <div className="relative" data-rich-menu>
                    <ToolbarButton title={t('إدراج جدول', 'Insert table')} active={openMenu === 'table'} onClick={() => setOpenMenu(openMenu === 'table' ? '' : 'table')}><MdTableChart size={17} /></ToolbarButton>
                    {openMenu === 'table' && (
                        <div className="absolute top-8 z-[90] bg-white border border-slate-200 rounded-lg shadow-lg p-2" style={{ insetInlineStart: 0 }} onMouseLeave={() => setTableHover({ rows: 0, cols: 0 })}>
                            <div className="grid grid-cols-8 gap-0.5" dir="ltr">
                                {Array.from({ length: 64 }, (_, index) => {
                                    const r = Math.floor(index / 8) + 1;
                                    const c = (index % 8) + 1;
                                    const on = r <= tableHover.rows && c <= tableHover.cols;
                                    return (
                                        <button
                                            key={index}
                                            type="button"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onMouseEnter={() => setTableHover({ rows: r, cols: c })}
                                            onClick={() => insertTable(r, c)}
                                            className={`w-4 h-4 rounded-sm border ${on ? 'bg-indigo-200 border-indigo-400' : 'bg-white border-slate-200'}`}
                                        />
                                    );
                                })}
                            </div>
                            <div className="mt-1.5 text-center text-[10px] font-bold text-slate-500">
                                {tableHover.rows ? `${tableHover.rows} × ${tableHover.cols}` : t('اختر حجم الجدول', 'Pick table size')}
                            </div>
                        </div>
                    )}
                </div>
                <ToolbarButton title={t('خط فاصل', 'Horizontal line')} onClick={() => exec('insertHorizontalRule')}><MdHorizontalRule size={17} /></ToolbarButton>
                <ToolbarButton title={t('اقتباس', 'Quote')} active={blockValue === 'blockquote'} onClick={() => exec('formatBlock', blockValue === 'blockquote' ? '<p>' : '<blockquote>')}><MdFormatQuote size={17} /></ToolbarButton>
                <ToolbarButton title={t('كود', 'Code block')} active={blockValue === 'pre'} onClick={() => exec('formatBlock', blockValue === 'pre' ? '<p>' : '<pre>')}><MdCode size={17} /></ToolbarButton>
                <Divider />
                <ToolbarButton title={t('مسح التنسيق', 'Clear formatting')} onClick={() => { exec('removeFormat'); exec('formatBlock', '<p>'); }}><MdFormatClear size={17} /></ToolbarButton>
                <ToolbarButton title={fullscreen ? t('تصغير (Esc)', 'Exit fullscreen (Esc)') : t('ملء الشاشة', 'Fullscreen')} active={fullscreen} onClick={() => setFullscreen((v) => !v)}>
                    {fullscreen ? <MdFullscreenExit size={17} /> : <MdFullscreen size={17} />}
                </ToolbarButton>
            </div>

            <div className={`relative ${fullscreen ? 'flex-1 min-h-0 flex flex-col' : ''}`}>
                {placeholder && isEmpty && !isFocused && (
                    <div
                        className="pointer-events-none absolute top-3 left-3 right-3 text-sm text-slate-400"
                        style={{ direction: dir, textAlign: resolvedTextAlign }}
                    >
                        {placeholder}
                    </div>
                )}
                <div
                    id={id}
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    className="mw-rich-content mw-rich-editor px-3 py-2.5 text-sm text-slate-700"
                    onFocus={() => { setIsFocused(true); rememberSelection(); refreshToolbarState(); }}
                    onBlur={() => { rememberSelection(); setIsFocused(false); emitChange(); }}
                    onInput={() => { rememberSelection(); emitChange(); }}
                    onKeyDown={onKeyDown}
                    onKeyUp={rememberSelection}
                    onMouseUp={rememberSelection}
                    onPaste={onPaste}
                    dir={dir}
                    style={{
                        minHeight: fullscreen ? undefined : minHeightPx,
                        maxHeight: fullscreen ? undefined : '70vh',
                        flex: fullscreen ? 1 : undefined,
                        overflowY: 'auto',
                        resize: fullscreen ? 'none' : 'vertical',
                        outline: 'none',
                        lineHeight: 1.8,
                        direction: dir,
                        textAlign: resolvedTextAlign,
                    }}
                />
            </div>
            <div className="flex items-center justify-between gap-2 px-3 py-1 border-t border-slate-100 text-[10px] text-slate-400 select-none" dir={isAr ? 'rtl' : 'ltr'}>
                <span>{t('Ctrl+Shift+V للصق كنص بدون تنسيق', 'Ctrl+Shift+V pastes as plain text')}</span>
                <span>{t(`${counts.words} كلمة · ${counts.chars} حرف`, `${counts.words} words · ${counts.chars} chars`)}</span>
            </div>
        </div>
        </>
    );
};

export default MwRichEditor
